import { notification, Spin } from '@pankod/refine-antd';
import { file2Base64, useNavigation } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { ProductForm } from 'components/product';
import { ICategory, IMeasurementUnit, ITag } from 'interfaces';
import { useEffect, useState } from 'react';
import { getAllCategoryDropdown } from 'services/category';
import { getMeasurementUnitsDropdown } from 'services/measurement-units';
import {
  getVariantsOfProduct,
  IVariantOfProduct,
  ProductWithVariantsData,
  updateProductWithVariants,
} from 'services/product';
import { getAllTagDropdown } from 'services/tag';
import { getTemplates, ITemplate } from 'services/template';
import { getWarehousesDropdown, IWarehouseDropDown } from 'services/warehouse';

export const ProductEditTab = ({
  handleBomTabEnabled,
}: {
  handleBomTabEnabled: (enabled: boolean) => void;
}) => {
  const [categoryOptions, setCategoryOptions] = useState<ICategory[]>([]);
  const [tagOptions, setTagOptions] = useState<ITag[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<IWarehouseDropDown[]>([]);
  const [templateOptions, setTemplateOptions] = useState<ITemplate[]>([]);
  const [measurementUnitOptions, setMeasurementUnitOptions] = useState<
    Omit<IMeasurementUnit, 'description'>[]
  >([]);
  const [product, setProduct] = useState<IVariantOfProduct | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const { list } = useNavigation();
  const { id: productId } = useParams();

  useEffect(() => {
    async function fetchData(productId: string) {
      try {
        setIsLoading(true);
        const [
          categoryResponse,
          tagResponse,
          warehouseResponse,
          templateResponse,
          productResponse,
          measurementUnitsResponse,
        ] = await Promise.all([
          getAllCategoryDropdown(),
          getAllTagDropdown(),
          getWarehousesDropdown(),
          getTemplates(),
          getVariantsOfProduct(productId),
          getMeasurementUnitsDropdown(),
        ]);

        setCategoryOptions(categoryResponse);
        setTagOptions(tagResponse);
        setWarehouseOptions(warehouseResponse);
        setTemplateOptions(templateResponse);
        setMeasurementUnitOptions(measurementUnitsResponse);
        setProduct(productResponse);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      } finally {
        setIsLoading(false);
      }
    }

    if (productId) {
      fetchData(productId);
    }
  }, [productId]);

  const handleFormSubmit = async (values: any) => {
    const { hasBatch: _, hasVariant: __, variantDetails, ...restFormValues } = values;
    const payload: ProductWithVariantsData = {
      ...restFormValues,
      variantsDetails: variantDetails.map((variant: any) => {
        const { attributeFields, customFields, templateFields } = variant;

        // make attribute payload
        const attributeKeys = Object.keys(attributeFields ?? {});
        const modifiedAttributeFields = attributeKeys.map((key) => ({
          key,
          value: attributeFields[key],
        }));

        // make custom field payload
        const customFiledKeys = Object.keys(customFields ?? {});
        const modifiedCustomFields = customFiledKeys
          .map((key) => {
            const customFieldValue = customFields[key];
            if (customFieldValue !== undefined) {
              return {
                key,
                value: customFieldValue.toString(),
              };
            } else {
              return undefined;
            }
          })
          .filter(Boolean);

        // make template field payload
        const templateFieldKeys = Object.keys(templateFields ?? {});
        const modifiedTemplateFields = templateFieldKeys.map((key) => ({
          templateId: key,
          templateAttributeData: templateFields[key],
        }));

        return {
          ...variant,
          sellingPrice: variant.sellingPrice ?? 0,
          attributeFields: modifiedAttributeFields,
          customFields: modifiedCustomFields,
          templateFields: modifiedTemplateFields,
        };
      }),
    };

    // modify image payload to base64string
    const { variantsDetails } = payload;
    for (const variant of variantsDetails) {
      const { photo } = variant;
      let modifiedPhoto: any = null;

      if (photo && Array.isArray(photo) && photo[0]?.url) {
        // modifiedPhoto = photo[0].url;
        modifiedPhoto = null;
      }

      if (photo && Array.isArray(photo) && !photo[0]?.url) {
        const imageFile = variant.photo[0]?.status !== 'error' ? variant.photo[0] : null;
        if (imageFile) {
          if (imageFile.originFileObj) {
            const base64String = await file2Base64(imageFile);
            const { originFileObj, response, status, xhr, ...rest } = imageFile;
            modifiedPhoto = { ...rest, base64String };
          } else {
            modifiedPhoto = imageFile;
          }
        }
      }

      variant.photo = modifiedPhoto;
    }

    setIsCreating(true);
    try {
      await updateProductWithVariants(productId!, payload);
      notification.success({
        message: 'Success',
        description: 'Successfully updated.',
      });
      list('products');
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsCreating(false);
  };

  return (
    <>
      {isLoading ? (
        <Spin
          spinning
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
          }}
        />
      ) : (
        <>
          <ProductForm
            type="edit"
            isFetching={isLoading}
            isSubmitting={isCreating}
            categoryOptions={categoryOptions}
            tagOptions={tagOptions}
            warehouseOptions={warehouseOptions}
            templateOptions={templateOptions}
            measurementUnitOptions={measurementUnitOptions}
            productInfo={product}
            onSubmit={handleFormSubmit}
            handleBomTabEnabled={handleBomTabEnabled}
          />
        </>
      )}
    </>
  );
};
